/// <reference types="google.maps" />

import { CONFIGURED_GOOGLE_PLACES_API_KEY } from "_shared/_utilities/meta";
import { useEffect, useState } from "react";

/**
 * Loads the Google Maps JavaScript API together with its Places library, and reports whether it
 * is usable.
 *
 * This stands in for `useLoadScript` from `@react-google-maps/api`. That hook asserts, from
 * inside a passive effect where no caller can catch it, that `window.google` exists once it has
 * marked itself loaded — and when the assertion fails the `Invariant Violation` escapes to the
 * nearest error boundary and takes the whole route down with it. Every caller already degrades
 * to manual address entry when the location service is unavailable, so that same condition is
 * reported here as {@link GooglePlacesApiState.loadError} rather than thrown.
 */
export interface GooglePlacesApiState {
  isLoaded: boolean;
  loadError: Error | undefined;
}

const SCRIPT_ELEMENT_ID = "google-places-api";
const READY_CALLBACK_NAME = "__attorneyHubGooglePlacesReady";
const LIBRARIES = ["places"];
const API_VERSION = "weekly";

/**
 * How long the script is given to call back before the location service is treated as
 * unavailable. Google answers a rejected key — wrong referrer, Places not enabled, billing
 * disabled — with a script that loads successfully and then never invokes the callback, so
 * without this the field would spin for the life of the page.
 */
const LOAD_TIMEOUT_MS = 15_000;

type GlobalWithReadyCallback = typeof globalThis & Record<typeof READY_CALLBACK_NAME, (() => void) | undefined>;

let pendingLoad: Promise<void> | undefined;

function placesApiIsPresent(): boolean {
  return Boolean(globalThis.google?.maps?.places);
}

function buildScriptUrl(apiKey: string): string {
  const parameters = new URLSearchParams({
    key: apiKey,
    v: API_VERSION,
    libraries: LIBRARIES.join(","),
    loading: "async",
    callback: READY_CALLBACK_NAME,
  });
  return `https://maps.googleapis.com/maps/api/js?${parameters.toString()}`;
}

function loadPlacesApi(): Promise<void> {
  if (placesApiIsPresent()) return Promise.resolve();
  if (pendingLoad) return pendingLoad;

  const load = new Promise<void>((resolve, reject) => {
    const apiKey = CONFIGURED_GOOGLE_PLACES_API_KEY;
    if (!apiKey) {
      reject(new Error("VITE_GOOGLE_PLACES_API_KEY is not set, so the Google Places API cannot be loaded."));
      return;
    }

    const globalScope = globalThis as GlobalWithReadyCallback;
    let timeoutId: ReturnType<typeof globalThis.setTimeout> | undefined;

    const settle = (failure?: Error) => {
      if (timeoutId !== undefined) globalThis.clearTimeout(timeoutId);
      // Google can still invoke the JSONP callback after our timeout or fetch-error path has
      // settled. Keep a callable no-op behind so that late script execution cannot throw into
      // the page's global error handler.
      globalScope[READY_CALLBACK_NAME] = () => undefined;
      if (failure) {
        reject(failure);
      } else {
        resolve();
      }
    };

    globalScope[READY_CALLBACK_NAME] = () => {
      // The condition `useLoadScript` asserts on. A script that announces itself ready without
      // leaving the global behind is a broken location service, not a broken application.
      if (placesApiIsPresent()) {
        settle();
      } else {
        settle(new Error("The Google Places API script signalled that it was ready, but window.google.maps.places is not present."));
      }
    };

    // A previous attempt can leave its element behind; the id is what keeps a StrictMode
    // double-mount, or a hot reload, from stacking a second copy of the API on the page.
    document.getElementById(SCRIPT_ELEMENT_ID)?.remove();

    const script = document.createElement("script");
    script.id = SCRIPT_ELEMENT_ID;
    script.async = true;
    script.src = buildScriptUrl(apiKey);
    script.addEventListener("error", () => {
      settle(new Error("The Google Places API script could not be fetched."));
    });

    timeoutId = globalThis.setTimeout(() => {
      settle(new Error(`The Google Places API did not finish loading within ${LOAD_TIMEOUT_MS}ms.`));
    }, LOAD_TIMEOUT_MS);

    document.head.appendChild(script);
  });

  pendingLoad = load;

  // A failure is deliberately not cached. Whatever blocked the script — an offline moment, a
  // proxy, an extension — may be gone by the time the next field mounts, and that mount should
  // get a fresh attempt instead of inheriting this one's error.
  void load.catch((error: unknown) => {
    console.warn("The Google Places API is unavailable; address fields fall back to manual entry.", error);
    if (pendingLoad === load) {
      pendingLoad = undefined;
    }
  });

  return load;
}

function asError(error: unknown): Error {
  if (error instanceof Error) return error;
  return new Error(String(error));
}

export default function useGooglePlacesApi(): GooglePlacesApiState {
  const [isLoaded, setIsLoaded] = useState(placesApiIsPresent);
  const [loadError, setLoadError] = useState<Error | undefined>(undefined);

  useEffect(() => {
    let active = true;
    void loadPlacesApi().then(
      () => {
        if (active) setIsLoaded(true);
      },
      (error: unknown) => {
        if (active) setLoadError(asError(error));
      },
    );
    return () => {
      active = false;
    };
  }, []);

  return { isLoaded, loadError };
}
